import URLS from "@/autogen/urls";

import type { PreviewMedium } from "./types";

// Backend's RenderResult uses `result` for the email medium; everything
// else is keyed by medium name. Remap on the way out so the rest of the
// codebase only sees medium names.
const RAW_KEY_BY_MEDIUM: Record<
  PreviewMedium,
  "result" | "web" | "portal" | "subscribe" | "archive"
> = {
  archive: "archive",
  email: "result",
  portal: "portal",
  subscribe: "subscribe",
  web: "web",
};

type FetchOpts = {
  signal: AbortSignal;
  headers: Record<string, string>;
};

// POSTs to /v1/render. Composable-side adapters fill the body with
// kind-specific fields; this helper just owns the wire shape and the
// medium-name remap on the response.
export const fetchRender = async <M extends PreviewMedium>(
  body: Record<string, unknown> & { context: M },
  { signal, headers }: FetchOpts
): Promise<string> => {
  const { context, ...rest } = body;

  const response = await fetch(URLS["render"](), {
    method: "POST",
    signal,
    headers: {
      ...headers,
      accept: "application/json",
      "content-type": "application/json",
    },
    body: JSON.stringify({
      ...rest,
      previews: [context],
    }),
  });

  if (!response.ok) {
    throw new Error(`Render failed (${response.status})`);
  }

  const raw = (await response.json()) as Partial<
    Record<"result" | "web" | "portal" | "subscribe" | "archive", string>
  >;

  return raw[RAW_KEY_BY_MEDIUM[context]] ?? "";
};

// POSTs to /v1/render-subscribe-template. Returns subscribe-medium-only.
export const fetchSubscribeTemplate = async (
  body: { newsletter: unknown },
  { signal, headers }: FetchOpts
): Promise<string> => {
  const response = await fetch(URLS["render-subscribe-template"](), {
    method: "POST",
    signal,
    headers: {
      ...headers,
      accept: "application/json",
      "content-type": "application/json",
    },
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    throw new Error(`Render subscribe template failed (${response.status})`);
  }

  const raw = (await response.json()) as { result: string };
  return raw.result;
};
